// PdfMarkupFrame.tsx — a real PDF you can run a marker over, on the board.
//
// WHY THIS EXISTS ALONGSIDE <PresentationFrame>, NOT INSTEAD OF IT.
// PresentationFrame shows the SAME pages as pre-rendered stills and its header
// explains why: "an embedded PDF viewer inside a React Flow canvas fights the
// canvas for wheel and click events, ships a heavy worker, and renders its own
// chrome we cannot style." That reasoning was right and still is — for reading
// a deck at a glance. It is the wrong trade for the thing Erez actually asked
// for on 16 Aug 2026: sitting with Outform's 24 pages the way he marked up
// textbooks at university, running a yellow marker over each section as he
// settles it. You cannot select a sentence in a JPEG.
//
// So: both. The stills stay for glancing; this is for working. The three
// objections in that header are answered rather than ignored —
//
//   fights the canvas → `nowheel nodrag nopan` + stopPropagation, exactly as
//                       its sibling does, so scrolling pages never pans.
//   heavy worker      → pdfjs is loaded by dynamic import INSIDE an effect, so
//                       it is a separate chunk that costs nothing until this
//                       node mounts.
//   chrome we cannot  → there is none. No toolbar, no buttons, no sidebar.
//   style               A page count, and that is the entire interface.
//
// ── THE INTERACTION: ONE GESTURE, AND ONLY ONE (Erez, 16 Aug 2026) ───────────
//
//   click a paragraph → yellow
//   click it again    → not yellow
//
// That is the whole thing. "So then I can just read a whole paragraph, click,
// read a point, click, read a paragraph, click."
//
// TWO EARLIER INTERACTIONS WERE DELETED TO GET HERE, and their removal is the
// point rather than a side effect: drag-to-select painted a highlight, and
// double-clicking a word painted a different one. Both worked. Having both
// meant the same document responded two ways to two gestures a reader makes
// without deciding to, so marking up a page required thinking about the tool.
// A marker you have to think about is not a marker. Text selection is now
// switched OFF for the same reason — a drag that paints a blue selection doing
// nothing is noise pretending to be a feature.
//
// A "PARAGRAPH" IS NOT SOMETHING PDF.JS HAS. It gives you positioned spans,
// one per text run, with no notion of a block — so the grouping below is the
// real work of this file. Spans are gathered into lines by vertical position,
// and lines into blocks by the gap between them and by their left edge, which
// is what separates a body paragraph from the bullet beneath it.
//
// Marks are rows in `doc_highlights`, not browser state, because the agent
// walking the document needs to read what a human has actually settled. See
// sql/052_doc_highlights.sql. A block is matched to a saved mark by its TEXT,
// not by an index or a coordinate: text survives a re-render at another zoom,
// on another screen, and a revision that moves the paragraph down a page.
//
// GEOMETRY IS PERCENT, NEVER PIXELS. A rect stored in pixels is correct once,
// on the screen that made it, and silently wrong at any other width or zoom.
//
// ── FOUR CAUSES OF "IT DOES NOT WORK", AND WHY THERE IS NO TEXT LAYER ────────
// This shipped broken four times. Every cause is worth keeping, because three
// of them are invisible from the code and the fourth is the reason the design
// changed.
//
// 1. `.react-flow__node { user-select: none }` — @xyflow/react forbids
//    selection inside a node, so the original drag-to-select gesture could
//    never have worked here. Moot now that the gesture is a click, but the
//    next person to add a text interaction will hit it.
//
// 2. PDF.JS SIZES ITS TEXT LAYER FROM `--total-scale-factor`, which its
//    stylesheet defines in exactly one place — `.pdfViewer .page`, its own
//    viewer chrome, which we do not use. Undefined variable, invalid calc(),
//    every span mis-sized. Moot now, for the reason in 4.
//
// 3. EVERY WRITE 401'd. Bare `fetch` against an API whose middleware requires
//    `Authorization: Bearer <workos jwt>` and reads no cookie. Not one mark
//    ever reached the database. `useApi()` is the app's one right answer.
//
//    WHAT COST THE TIME WAS NOT THE MISSING HEADER but that the failure path
//    rolled the mark back and said NOTHING — so a 401, a 500, a dropped
//    connection and a rejected payload produced one identical symptom. Same
//    defect this session had just fixed in `capture/gate.py`. Every failure
//    now names itself on the frame, in words.
//
// 4. AND THE ONE THAT MATTERED MOST: THE BLOCKS WERE MEASURED FROM THE SCREEN.
//    They came from `getBoundingClientRect()` on rendered spans, which is
//    screen geometry — and inside React Flow the board sits under a CSS
//    transform. At the zoom Erez actually reads at, a line of text is about
//    THREE PIXELS tall, so every threshold in the grouping became sub-pixel
//    noise: page 1 fragmented from 23 blocks into 48 slivers and a paragraph
//    became a 156×3 target no hand can hit. It tested perfectly at 1:1 in a
//    harness and was unusable on the board.
//
//    Fixed by measuring the DOCUMENT instead of the screen —
//    `page.getTextContent()` through the page's own viewport at scale 1.
//
//    THAT IS ALSO WHY THERE IS NO TEXT LAYER ANY MORE. It existed to be
//    selected (gesture gone) and to be measured (now measured from the
//    document), so it earns nothing and costs an invisible DOM layer that has
//    to stay in alignment. Deleting it takes causes 1 and 2 with it, and the
//    blocks no longer depend on anything having been painted at all.

import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/lib/api";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const MARKER = "rgba(250, 214, 62, 0.45)";

export type Rect = { x: number; y: number; w: number; h: number };

export type Highlight = {
  id: string;
  page: number;
  rects: Rect[];
  quote: string | null;
  color: string;
};

/** A paragraph, a bullet, or a heading — one clickable unit. */
export type Block = {
  page: number;
  /** One rect per line, in percent — the marker follows the text. */
  rects: Rect[];
  /** The union, in percent — the click target, so gaps between lines are live. */
  hit: Rect;
  /** Whitespace-collapsed text. The identity of the block. */
  text: string;
};

/** Render at 2x for a sharp canvas on retina, then lay it out at 1x. */
const RASTER = 2;

/**
 * How many pages either side of the one you are reading keep their raster.
 *
 * ⚠ THIS NUMBER IS WHY THE BOARD USED TO GO BLANK AT DEEP ZOOM (Erez, 17 Aug
 * 2026). Every painted page is a canvas of `width × RASTER` by the same over
 * the page aspect — on this board 1760 × 2278, which is **15.3 MB of backing
 * store each**. Before this constant existed nothing ever released one: the
 * `painted` set only guaranteed a page was never painted TWICE. Read the whole
 * 24-page blueprint and you were holding **367 MB of canvas**.
 *
 * That memory does not sit in a quiet corner. This frame lives inside
 * `.react-flow__viewport`, the element React Flow applies `transform: scale(k)`
 * to, so at zoom 2.5 the compositor has to re-rasterise the entire subtree at
 * 6.25× the pixels. Chrome stops trying and drops the layer — and a dropped
 * layer looks exactly like EVERYTHING ON THE BOARD DISAPPEARING. Nothing
 * errors, nothing logs, and the board comes back when you zoom out.
 *
 * The diagnostic that mattered: Falcon's board never did this, and Falcon's
 * grid is 20000px wide against Outform's 6000 — three times the geometry and no
 * failure. That killed "the layer is too big" and left the one thing Outform
 * has and Falcon does not: a PDF.
 *
 * 2 keeps five pages (~77 MB), five times under where it failed, and wide
 * enough that ordinary scrolling never waits for a repaint.
 */
const KEEP_PAINTED = 2;

/** Collapse runs of whitespace so a block's identity does not depend on how
 *  pdf.js happened to split its runs. */
export const norm = (s: string) => s.replace(/\s+/g, " ").trim();

/**
 * pdf.js text items → boxes in the page's own coordinate space.
 *
 * `item.transform` places the run's BASELINE in PDF user space, y up.
 * `Util.transform` composes it with the viewport's matrix, which is what flips
 * the axis and gives CSS-style top-left coordinates. The glyph height is the
 * length of the matrix's vertical vector, not `item.height`, which is 0 for
 * many runs.
 */
export function placeItems(
  items: { str: string; transform: number[]; width: number; height: number }[],
  viewport: { transform: number[] },
  Util: { transform(a: number[], b: number[]): number[] },
): Placed[] {
  const out: Placed[] = [];
  for (const it of items) {
    if (!it.str || !it.str.trim()) continue;
    const tx = Util.transform(viewport.transform, it.transform);
    const h = Math.hypot(tx[2], tx[3]) || it.height || 10;
    // A run with no height or no width is a spacing artifact, not text. Kept
    // out here rather than downstream, because a degenerate item poisons the
    // median line height that every grouping threshold is measured against —
    // and if one survives into a block it becomes a target nobody can click.
    if (h <= 0.5 || it.width <= 0) continue;
    const left = tx[4];
    const top = tx[5] - h;          // tx[5] is the baseline; the box sits above it
    out.push({ text: it.str, left, right: left + it.width, top, bottom: top + h, h });
  }
  return out;
}

/** One text run, placed in the PAGE's own coordinate space (CSS px at scale 1). */
type Placed = { text: string; left: number; right: number; top: number; bottom: number; h: number };

/**
 * Text runs → lines → blocks.
 *
 * ⚠ MEASURED FROM THE DOCUMENT, NEVER FROM THE SCREEN — and the first two
 * versions of this got that wrong in a way that only showed up on the board.
 * They read `getBoundingClientRect()` off the rendered spans, which is screen
 * geometry: inside React Flow the whole board is under a CSS transform, so at
 * board zoom a line of text is about THREE PIXELS tall. Every threshold below
 * is a comparison between small distances, and at three pixels those
 * comparisons dissolve into sub-pixel noise — page 1 fragmented from 23 blocks
 * into 48 slivers, and a "paragraph" became a 156×3 target no hand can hit.
 * It tested perfectly at 1:1 and was unusable at the zoom Erez actually reads
 * at, which is why four fixes shipped before this one.
 *
 * So the geometry now comes from `page.getTextContent()` through the page's
 * OWN viewport at scale 1. It is identical whatever the board is doing, needs
 * no rendered DOM, and can be computed before anything is painted.
 *
 * Three rules, all measured against the page's own median line height:
 *
 *   a vertical gap wider than a line → a new block (paragraph spacing)
 *   a line opening with a bullet     → a new block (a point is a unit)
 *   a line that DEDENTS              → a new block (back out to the margin)
 *
 * ⚠ THE ASYMMETRY IS THE WHOLE TRICK, and the first version got it wrong.
 * It split on ANY change of left edge, which looked reasonable and broke every
 * wrapped bullet in half — "• Slow turnaround: … reducing" became one block and
 * "competitive win rates." became another, so a point took two clicks and the
 * second one looked like a stray. That is a HANGING INDENT: the bullet glyph
 * hangs to the left and the wrapped text aligns under the words, so a
 * continuation line always steps RIGHT. Stepping right is therefore evidence of
 * belonging, not of separation. Only a DEDENT starts something new.
 *
 * Deliberately not clever beyond that. Font-size heuristics and per-level list
 * detection start guessing at intent, and a marker that highlights something
 * other than what you clicked is worse than one that occasionally splits a
 * paragraph in two.
 */
export function blocksFrom(items: Placed[], pageW: number, pageH: number, page: number): Block[] {
  const hostBox = { width: pageW, height: pageH };
  if (!hostBox.width || !hostBox.height || !items.length) return [];
  items.sort((a, b) => a.top - b.top || a.left - b.left);

  const heights = items.map((i) => i.h).sort((a, b) => a - b);
  const medianH = heights[Math.floor(heights.length / 2)] || 12;

  // ── spans → lines ──────────────────────────────────────────────────────────
  type Line = { top: number; bottom: number; left: number; right: number; text: string[] };
  const lines: Line[] = [];
  for (const it of items) {
    const ln = lines[lines.length - 1];
    // Same line if the baselines are within half a line of each other. Superscripts
    // and inline size changes shift `top` a little; half a line absorbs that
    // without swallowing the next line.
    if (ln && it.top < ln.bottom - it.h * 0.5) {
      ln.top = Math.min(ln.top, it.top);
      ln.bottom = Math.max(ln.bottom, it.bottom);
      ln.left = Math.min(ln.left, it.left);
      ln.right = Math.max(ln.right, it.right);
      ln.text.push(it.text);
    } else {
      lines.push({ top: it.top, bottom: it.bottom, left: it.left, right: it.right, text: [it.text] });
    }
  }

  // ── lines → blocks ─────────────────────────────────────────────────────────
  const out: Block[] = [];
  let cur: Line[] = [];

  const flush = () => {
    if (!cur.length) return;
    const text = norm(cur.map((l) => l.text.join(" ")).join(" "));
    if (text) {
      const rects = cur.map((l) => ({
        x: (l.left / hostBox.width) * 100,
        y: (l.top / hostBox.height) * 100,
        w: ((l.right - l.left) / hostBox.width) * 100,
        h: ((l.bottom - l.top) / hostBox.height) * 100,
      }));
      const left = Math.min(...cur.map((l) => l.left));
      const right = Math.max(...cur.map((l) => l.right));
      const top = Math.min(...cur.map((l) => l.top));
      const bottom = Math.max(...cur.map((l) => l.bottom));
      out.push({
        page,
        rects,
        hit: {
          x: (left / hostBox.width) * 100,
          y: (top / hostBox.height) * 100,
          w: ((right - left) / hostBox.width) * 100,
          h: ((bottom - top) / hostBox.height) * 100,
        },
        text,
      });
    }
    cur = [];
  };

  // A line that opens a list item. Their document uses •, – and o; `o` is
  // excluded on purpose, because a body line beginning with the word "or" or a
  // stray "o" would split a paragraph for no reason, and a missed sub-bullet
  // costs one extra click while a false split costs trust in the marker.
  const opensItem = (s: string) => /^\s*[•▪◦‣·–—]\s+/.test(s);

  for (const ln of lines) {
    if (cur.length) {
      const prev = cur[cur.length - 1];
      const gap = ln.top - prev.bottom;
      const text = ln.text.join(" ");
      // Dedent only: a continuation under a hanging indent steps RIGHT, and
      // stepping right means it belongs to what is above it.
      const dedented = ln.left < Math.min(...cur.map((l) => l.left)) - medianH * 0.5;
      if (gap > medianH * 0.7 || opensItem(text) || dedented) flush();
    }
    cur.push(ln);
  }
  flush();

  return out;
}

/**
 * Every clickable block in a PDF, page by page — WITHOUT DRAWING ANYTHING.
 *
 * This is the half of this component that turned out to be reusable. The board's
 * coverage map already shows each page as a pre-rendered still from R2, so it
 * needs the text GEOMETRY and nothing else: `getTextContent()` through the page's
 * own viewport at scale 1, in percent, which is identical whatever size the page
 * is drawn at.
 *
 * ⭐ AND IT MEANS THE MARKER COSTS NO CANVAS AT ALL. Rasterising is what put
 * 367 MB of bitmap on the board this morning and made it go blank past 2.5x
 * zoom; the stills carry the picture and pdf.js only supplies the coordinates.
 * The feature gets richer and the thing that broke the board is simply absent.
 */
export async function blocksForDoc(url: string): Promise<Record<number, Block[]>> {
  const pdfjs = await import("pdfjs-dist");
  const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjs.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;

  const doc = await pdfjs.getDocument({ url }).promise;
  const out: Record<number, Block[]> = {};
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const css = page.getViewport({ scale: 1 });
    const placed = placeItems((await page.getTextContent()).items as never[], css, pdfjs.Util);
    out[n] = blocksFrom(placed, css.width, css.height, n);
  }
  return out;
}

export function PdfMarkupFrame({
  url,
  docSlug,
  label,
  caption,
  width = 900,
  height = 1180,
  createdBy = "erez",
}: {
  /** The PDF itself. Must be CORS-readable — R2 public buckets send `*`. */
  url: string;
  /** Stable key the marks hang off, e.g. "outform/blueprint-rev10". Changing
   *  it orphans every existing mark, so treat it as permanent. */
  docSlug: string;
  label?: string;
  caption?: string;
  width?: number;
  height?: number;
  createdBy?: string;
}) {
  const [numPages, setNumPages] = useState(0);
  const [aspect, setAspect] = useState(1275 / 1650);      // US Letter until page 1 says otherwise
  const [marks, setMarks] = useState<Highlight[]>([]);
  const [blocks, setBlocks] = useState<Record<number, Block[]>>({});
  const [error, setError] = useState<string | null>(null);
  // Saving failures are shown, never swallowed — see the header, cause 3.
  const [problem, setProblem] = useState<string | null>(null);
  const [visiblePage, setVisiblePage] = useState(1);

  // `useApi` returns a fresh closure each render, so holding it in a ref keeps
  // the effects and callbacks below dependency-stable.
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const docRef = useRef<{ pdfjs: any; doc: any } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const painted = useRef<Set<number>>(new Set());

  // ── the marks ──────────────────────────────────────────────────────────────

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await apiRef.current(`/api/doc-highlights?doc=${encodeURIComponent(docSlug)}`);
        if (!res.ok) throw new Error(`${res.status}`);
        const rows = (await res.json()) as Highlight[];
        if (live) { setMarks(Array.isArray(rows) ? rows : []); setProblem(null); }
      } catch (e) {
        // An unreachable API must not stop the document rendering — but it must
        // not pretend the page has no marks on it either.
        if (live) setProblem(`could not load existing marks (${e instanceof Error ? e.message : "failed"})`);
      }
    })();
    return () => { live = false; };
  }, [docSlug]);

  // ── the document ───────────────────────────────────────────────────────────

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Dynamic, so pdfjs and its worker are their own chunk. A board that
        // never opens this section never downloads it.
        const pdfjs = await import("pdfjs-dist");
        const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
        pdfjs.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;

        const doc = await pdfjs.getDocument({ url }).promise;
        if (!live) return;
        docRef.current = { pdfjs, doc };
        const first = await doc.getPage(1);
        const vp = first.getViewport({ scale: 1 });
        if (!live) return;
        setAspect(vp.width / vp.height);
        setNumPages(doc.numPages);
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : "could not open the PDF");
      }
    })();
    return () => { live = false; docRef.current = null; painted.current.clear(); };
  }, [url]);

  // ── paint a page when it comes into view, and RELEASE it when it leaves ────
  //
  // Twenty-four Letter pages rasterised at once is ~367 MB of canvas for a
  // document you read one page at a time. The comment here used to say
  // "roughly 100 MB" and treat that as the reason to paint lazily — it was a
  // guess, it was 3.7× low, and painting lazily was only ever half the answer.
  // A page that is painted once and never released is not lazy, it is deferred:
  // scroll the whole document and you arrive at exactly the state lazy painting
  // was supposed to avoid. See KEEP_PAINTED for what that cost us.

  /**
   * Drop a page's raster.
   *
   * `width = 0` is the line that actually frees the memory. Removing the
   * element only makes it garbage, and garbage that has not been collected yet
   * is still resident — which is the whole problem. Setting the dimension
   * releases the backing store synchronously.
   *
   * The marks survive: `blocks[n]` is React state, computed from the PDF's own
   * text content and never from the rendered canvas, so an evicted page keeps
   * every highlight and simply repaints when it scrolls back into view.
   */
  const release = useCallback((n: number) => {
    const host = pageRefs.current[n - 1];
    const c = host?.querySelector("canvas") as HTMLCanvasElement | null;
    if (c) { c.width = 0; c.height = 0; }
    host?.querySelector("[data-canvas]")?.replaceChildren();
    painted.current.delete(n);
  }, []);

  const paint = useCallback(async (n: number) => {
    const ctx = docRef.current;
    const host = pageRefs.current[n - 1];
    if (!ctx || !host || painted.current.has(n)) return;
    painted.current.add(n);

    try {
      const page = await ctx.doc.getPage(n);

      // The page's OWN viewport, scale 1. Everything about the blocks is
      // measured in here and nothing about them depends on how large the page
      // happens to be drawn or how far the board is zoomed out.
      const css = page.getViewport({ scale: 1 });

      // Blocks first, and deliberately before any painting: they need no
      // rendered DOM at all, so a page that fails to rasterise is still
      // clickable, and a board redraw can never shift them.
      const placed = placeItems((await page.getTextContent()).items as never[], css, ctx.pdfjs.Util);
      setBlocks((b) => ({ ...b, [n]: blocksFrom(placed, css.width, css.height, n) }));

      const vp = page.getViewport({ scale: (host.clientWidth / css.width) * RASTER });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(vp.width);
      canvas.height = Math.floor(vp.height);
      Object.assign(canvas.style, { width: "100%", height: "100%", display: "block" });
      const c2d = canvas.getContext("2d");
      if (!c2d) return;

      host.querySelector("[data-canvas]")?.replaceChildren(canvas);
      await page.render({ canvasContext: c2d, viewport: vp, canvas }).promise;
    } catch (e) {
      painted.current.delete(n);           // let a failed page try again later
      // Never swallow this. A page that silently fails to paint or to produce
      // blocks looks exactly like a page with nothing to mark, which is the
      // failure mode this whole file has now been bitten by three times.
      setProblem(`page ${n} — ${e instanceof Error ? e.message : "could not render"}`);
    }
  }, []);

  /**
   * Which page are we on — read from THIS FRAME'S OWN SCROLL, never from the
   * screen.
   *
   * ⚠ THIS REPLACED AN IntersectionObserver, AND THE OBSERVER IS WHY THE WHOLE
   * BOARD BLINKED WHILE YOU PINCHED (Erez, 17 Aug 2026). An observer is
   * screen-geometry: it recomputes whenever anything above it moves, and this
   * frame lives under `.react-flow__viewport`, which BoardCanvas rewrites
   * `transform: scale(k)` on for EVERY wheel event of a pinch. So every zoom
   * step re-ran the callback, which called `setVisiblePage` — a React state
   * update that re-renders 24 page hosts and every highlight rect — and asked
   * pages to paint. A DOM mutation inside the layer being scaled invalidates
   * the layer, so the flicker was not local to the PDF: the ENTIRE board
   * flashed, zooming in and zooming out, on the Outform board only.
   *
   * It was the only observer on either board. `grep -n "IntersectionObserver\\|
   * ResizeObserver" board/*.tsx board/falcon/*.tsx board/outform/*.tsx` returns
   * exactly one hit, this file — which is precisely why Falcon pinches clean
   * and has nothing to do with Falcon being simpler.
   *
   * Scroll position is a LAYOUT number. It does not change when an ancestor is
   * transformed, so zooming the board now costs this component literally
   * nothing. Same principle the block geometry above already follows, and for
   * the same reason: measure the document, never the screen. The observer was
   * the one screen-geometry dependency left in the file.
   */
  const syncVisible = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc || !numPages) return;
    // offsetTop is relative to the nearest positioned ancestor, which may not
    // be the scrollport — so measure every page against the first one rather
    // than assuming they share an origin with scrollTop.
    const base = pageRefs.current[0]?.offsetTop ?? 0;
    const mid = sc.scrollTop + sc.clientHeight / 2;
    let cur = 1;
    for (let i = 0; i < numPages; i++) {
      const el = pageRefs.current[i];
      if (!el) continue;
      if (el.offsetTop - base <= mid) cur = i + 1;
      else break;
    }
    setVisiblePage(cur);                       // same value = React bails, no render
    for (let n = Math.max(1, cur - KEEP_PAINTED); n <= Math.min(numPages, cur + KEEP_PAINTED); n++) {
      paint(n);
    }
    // Eviction lives HERE, not inside paint(). Two pages painting concurrently
    // each used to evict the other on completion, which is a blank-repaint loop
    // waiting to happen; driving it from one place, off the current page, makes
    // that impossible.
    for (const p of [...painted.current]) {
      if (Math.abs(p - cur) > KEEP_PAINTED) release(p);
    }
  }, [numPages, paint, release]);

  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc || !numPages) return;
    let queued = false;
    const onScroll = () => {
      // One sync per frame at most. A scroll burst is dozens of events and the
      // work behind this is a pdf.js render.
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; syncVisible(); });
    };
    sc.addEventListener("scroll", onScroll, { passive: true });
    syncVisible();                             // paint what is on screen at open
    return () => sc.removeEventListener("scroll", onScroll);
  }, [numPages, syncVisible]);

  // ── the one gesture ────────────────────────────────────────────────────────

  const toggle = useCallback(async (block: Block) => {
    // A block is marked if a saved mark on this page carries the same text.
    // Matching on text rather than on an index or a coordinate is what lets a
    // mark survive a re-render at another width, or the document being revised.
    const existing = marks.find((h) => h.page === block.page && norm(h.quote ?? "") === block.text);

    if (existing) {
      setMarks((m) => m.filter((h) => h.id !== existing.id));
      try {
        const res = await apiRef.current(`/api/doc-highlights?id=${existing.id}`, { method: "DELETE" });
        if (!res.ok) throw new Error(`${res.status}`);
        setProblem(null);
      } catch (e) {
        setMarks((m) => [...m, existing]);   // put it back; the row still exists
        setProblem(`not removed — ${e instanceof Error ? e.message : "request failed"}`);
      }
      return;
    }

    // Optimistic: the yellow lands under the cursor immediately and is
    // reconciled with the real row when the write returns. A marker that lags a
    // network round-trip does not feel like a marker.
    const temp: Highlight = {
      id: `temp-${Date.now()}`, page: block.page, rects: block.rects,
      quote: block.text, color: "yellow",
    };
    setMarks((m) => [...m, temp]);

    try {
      const res = await apiRef.current("/api/doc-highlights", {
        method: "POST",
        body: JSON.stringify({
          doc_slug: docSlug, page: block.page, rects: block.rects,
          quote: block.text, color: "yellow", created_by: createdBy,
        }),
      });
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 120)}`);
      const saved = (await res.json()) as Highlight;
      setMarks((m) => m.map((h) => (h.id === temp.id ? saved : h)));
      setProblem(null);
    } catch (e) {
      // Roll back rather than leave a yellow band that looks saved and is not —
      // what is on screen must be what is in the database. But SAY SO: a silent
      // rollback is a flash of yellow that a person reasonably reads as their
      // own mis-click, and it is how a missing Authorization header cost an
      // evening.
      setMarks((m) => m.filter((h) => h.id !== temp.id));
      setProblem(`not saved — ${e instanceof Error ? e.message : "request failed"}`);
    }
  }, [marks, docSlug, createdBy]);

  // ── frame ──────────────────────────────────────────────────────────────────

  const markedOn = (page: number) =>
    new Set(marks.filter((h) => h.page === page).map((h) => norm(h.quote ?? "")));

  // ── ORPHANS MUST BE VISIBLE, BECAUSE THEY ARE INVISIBLE ON THE PAGE ────────
  // A mark only paints when its text matches a block on the page, so a saved
  // row whose text matches nothing renders as NOTHING — it cannot be seen and
  // it cannot be clicked off, but it is still in the table. On 16 Aug that bit
  // twice over: eight rows left by the deleted double-click-a-word gesture sat
  // there unseen, the counter included them so it read 20 when 12 were real,
  // and I read the table and reported all twenty back to Erez as his marks. He
  // replied "I don't see any strays. What are you talking about?" — correctly.
  //
  // Orphans are legitimate as well as accidental: revise the PDF and marks stop
  // matching. Either way the answer is the same and it is the same answer as
  // everywhere else in this file — say it, do not swallow it.
  //
  // Only pages that have actually been grouped can be judged. A mark on a page
  // nobody has scrolled to yet is unknown, not orphaned.
  const orphans = marks.filter((h) => {
    const onPage = blocks[h.page];
    return onPage && !onPage.some((b) => b.text === norm(h.quote ?? ""));
  }).length;

  return (
    <div style={{ width, fontFamily: FONT, pointerEvents: "auto" }}>
      {label && (
        <div style={{ fontSize: 13, color: "#4A534D", marginBottom: 8, fontWeight: 600 }}>{label}</div>
      )}

      <div
        ref={scrollRef}
        className="nowheel nodrag nopan"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          pointerEvents: "auto",
          // Selection is deliberately OFF: the only gesture is a click on a
          // paragraph, and a drag that paints a blue selection doing nothing is
          // noise pretending to be a feature.
          userSelect: "none",
          WebkitUserSelect: "none",
          width, height, overflowY: "auto", overflowX: "hidden",
          background: "#E7E5E1", borderRadius: 10, padding: 16,
          border: "1px solid #D2CFC9",
        }}
      >
        {error && (
          <div style={{ fontFamily: MONO, fontSize: 12, color: "#8C4A4A", padding: 20 }}>{error}</div>
        )}
        {!error && !numPages && (
          <div style={{ fontFamily: MONO, fontSize: 12, color: "#8C948E", padding: 20 }}>
            opening the document…
          </div>
        )}

        {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => {
          const marked = markedOn(n);
          return (
            <div
              key={n}
              data-page={n}
              ref={(el) => { pageRefs.current[n - 1] = el; }}
              style={{
                position: "relative", width: "100%", aspectRatio: String(aspect),
                marginBottom: 16, background: "#FFFFFF",
                boxShadow: "0 1px 3px rgba(0,0,0,.12)", overflow: "hidden",
              }}
            >
              <div data-canvas style={{ position: "absolute", inset: 0 }} />

              {(blocks[n] ?? []).map((b, bi) => {
                const on = marked.has(b.text);
                return (
                  <div key={bi}>
                    {/* The marker follows the LINES, so it looks like a pen
                        rather than a box drawn round the paragraph. */}
                    {on && b.rects.map((r, ri) => (
                      <div
                        key={ri}
                        style={{
                          position: "absolute",
                          left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%`,
                          background: MARKER, mixBlendMode: "multiply",
                          borderRadius: 2, pointerEvents: "none",
                        }}
                      />
                    ))}
                    {/* One click target per block, covering the union — so the
                        gaps between lines are live and a click never falls
                        through into the page. */}
                    <div
                      onClick={(e) => { e.stopPropagation(); toggle(b); }}
                      title={on ? "click to un-mark" : "click to mark as covered"}
                      style={{
                        position: "absolute",
                        left: `${b.hit.x}%`, top: `${b.hit.y}%`,
                        width: `${b.hit.w}%`, height: `${b.hit.h}%`,
                        cursor: "pointer", background: "transparent",
                      }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div style={{
        display: "flex", alignItems: "center", gap: 14, marginTop: 8,
        fontFamily: MONO, fontSize: 11, color: "#8C948E",
      }}>
        <span>{visiblePage} / {numPages || "…"}</span>
        <span style={{ color: "#4A534D" }}>click a paragraph to mark it</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: MARKER, display: "inline-block" }} />
          {marks.length} marked
        </span>
        {orphans > 0 && (
          <span style={{ color: "#8C4A4A", fontWeight: 600 }}>
            ⚠ {orphans} saved {orphans === 1 ? "mark no longer matches" : "marks no longer match"} the text
          </span>
        )}
        {/* The whole point: a write that failed says so, here, in words. */}
        {problem && <span style={{ color: "#8C4A4A", fontWeight: 600 }}>⚠ {problem}</span>}
        {caption && !problem && <span style={{ marginLeft: "auto" }}>{caption}</span>}
      </div>
    </div>
  );
}
